import { useMemo } from 'react';
import { DropzoneOptions } from 'react-dropzone';
import { useTranslation } from '@sib/i18n';
import { Tooltip } from '@sib/ui';
import { useOnDropHandler } from '@sib/utils';
import { FileUploadStore } from '@spendbrain/stores/fileUploadStore';
import { DropzoneContent } from './DropzoneContent';

export interface UploadDocumentsDropzoneProps {
  allowedFormats: string;
  fileUploadStore: FileUploadStore;
  maxFilesSize: number;
  maxFilesNumber: number;
  disabled?: boolean;
  dropzoneConfig?: Partial<DropzoneOptions>;
  storageConnectionFailed?: boolean;
}

export const UploadDocumentsDropzone = ({
  allowedFormats,
  fileUploadStore,
  disabled,
  maxFilesSize,
  maxFilesNumber,
  dropzoneConfig,
  storageConnectionFailed,
}: UploadDocumentsDropzoneProps) => {
  const { t } = useTranslation();
  const { addFiles } = fileUploadStore;
  const { onDrop } = useOnDropHandler({
    addFiles,
    maxSize: `${maxFilesSize} MB`,
  });

  const dropzoneContent = useMemo(
    () => (
      <DropzoneContent
        dropzoneConfig={dropzoneConfig}
        onDrop={onDrop}
        disabled={disabled}
      />
    ),
    [dropzoneConfig, onDrop, disabled]
  );

  const DropzoneContentOptionalTooltip = useMemo(() => {
    if (storageConnectionFailed) {
      return (
        <Tooltip
          content={t('failedStorageConnection')}
          TooltipContentProps={{ variant: 'secondary' }}
          TooltipTriggerProps={{ asChild: false, className: 'w-full' }}
        >
          {dropzoneContent}
        </Tooltip>
      );
    }

    return dropzoneContent;
  }, [storageConnectionFailed, t, dropzoneContent]);

  return (
    <div className="flex items-center justify-between gap-1">
      <div className="flex flex-col gap-1 font-bold">
        <label className="text-primary text-sm">
          {t('uploadDocuments.title')}
        </label>
        <p className="text-muted-foreground text-xs leading-4 font-semibold">
          {t('uploadDocuments.description', {
            formats: allowedFormats,
          })}
        </p>
        <div className="flex gap-2">
          <p className="text-muted-foreground text-xs leading-4 font-semibold">
            {t('uploadDocuments.maxFilesNumber', {
              number: maxFilesNumber,
            })}
          </p>
          <p className="text-muted-foreground text-xs leading-4 font-semibold">
            {t('uploadDocuments.maxFilesSize', {
              size: maxFilesSize,
            })}
          </p>
        </div>
      </div>
      {DropzoneContentOptionalTooltip}
    </div>
  );
};
